/**
 * @fileOverview Modal.
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:array_from 'array-from'
 * @requires Polyfill:element_prototype_classlist 'classlist'
 * @requires Polyfill:element_prototype_matches 'matches'
 * @requires Polyfill:element_prototype_closest 'closest'
 */

export default class Modal {
  constructor( options = {}) {

    this.onAfterLoad = options.onAfterLoad ? options.onAfterLoad : () => {};

    this.modals = Array.from( document.querySelectorAll( '.modal' ));
    // Check if modal exist
    if ( this.modals ) {
      let dataTriggers = Array.from( document.querySelectorAll( '[data-toggle="modal"]' )),
        dataClose = Array.from( document.querySelectorAll( '[data-dismiss="modal"]' )),
        holders = Array.from( document.querySelectorAll( '.modal__holder' ));

      // Check if there are triggers
      if ( dataTriggers ) {
        dataTriggers.forEach( e => {
          // Add listener and toggle the modal on click
          e.addEventListener( 'click', e => {
            e.preventDefault();
            this._setCurrentTrigger( e.currentTarget );
            const targetId = this.lastTrigger.getAttribute( 'data-target' );

            // Add new hash to history
            history.pushState( null, null, '#' + targetId );
            // eslint-disable-next-line one-var
            const target = document.getElementById( targetId );
            this.open( target );
          });
        });
      }

      // Check if there are close buttons
      if ( dataClose ) {
        dataClose.forEach( e => {
          e.addEventListener( 'click', this.close.bind( this ));
        });
      }

      // Check if there are .modal__holders
      if ( holders ) {
        holders.forEach( e => {
          e.addEventListener( 'click', this._clickToClose.bind( this ));
        });
      }

      // Check if user navigates through browser history
      window.addEventListener( 'popstate', this.close.bind( this ));
      // Check if url change
      window.addEventListener( 'hashchange', this._openByHash.bind( this ));
      // Check if modal is open on load
      window.addEventListener( 'load', this._openByHash.bind( this ));
    }
  }

  /**
   * Open the selected modal
   * @method open
   * @param {Object} targetedModal - Modal node
   *
   */
  open( targetedModal ) {
    // Fast click doesn't call this function
    if ( document.querySelector( '.js-is--modal-open' )) {
      return false;
    }

    // Check is targetedModal is a HTML Element
    if ( targetedModal instanceof HTMLElement ) {
      this.lastModal = targetedModal;
    } else if ( typeof targetedModal === 'string' ) {
      // Check is targetedModal is a string
      this.lastModal = document.getElementById( targetedModal );
    }

    // Assign modal to lastModal
    // Check if the modal exist
    if ( this.lastModal ) {
      // Build and append overlay
      const overlay = this._buildOverlay();
      this._openOverlay( overlay );

      // Add event listener for ESC
      this.escListener = this._escToClose.bind( this );
      document.addEventListener( 'keyup', this.escListener );

      if ( this.lastTrigger ) {
        // Check if the button trigger an ajax modal
        if ( this.lastTrigger.hasAttribute( 'data-fetch-url' )) {
          // Attention!! Async Function.
          this._fetchContent( this.lastTrigger.getAttribute( 'data-fetch-url' ));
          this._setFetchModalSize();
        }
      }

      // Show the modal
      setTimeout(() => this.lastModal.classList.add( 'js-is--animated' ), 20 );
      // this.lastModal.classList.add('js-is--open')

      let holder = this.lastModal.parentElement.parentElement;
      holder.classList.add( 'js-is--open' );
      // holder.addEventListener('click', this.close.bind(this)); // Causing double close - ERROR

      // Remove the scroll from the body -> allow the modal to scroll instead
      document.body.classList.add( 'js-is--modal-open' );
      this.onAfterLoad( this.lastModal );

      // eslint-disable-next-line no-constant-condition
      if ( window.selectra.ds.lazyLoadInstance &&
        typeof ( window.selectra.ds.lazyLoadInstance === 'object' )) {
        window.selectra.ds.lazyLoadInstance.update();
      }

    } else {
      throw new Error( 'The targeted modal does not exist' );
    }
  }


  /**
   * Close the first modal
   * @method close
   */
  close( e ) {
    // Fast click doesn't call this function
    let modal = document.querySelector( '.js-is--open' );
    if ( !modal ) {
      return false;
    }

    // Add history step
    // switch(e.type) {
    //   case 'click' :
    //     let urlLocation = window.location.pathname;
    //     if (window.location.search) {
    //       urlLocation += window.location.search;
    //     }
    //     history.pushState(null, null, urlLocation);
    //     break;
    // }
    if ( e.type === 'click' || e.type === 'keyup' ) {
      let urlLocation = window.location.pathname;
      if ( window.location.search ) {
        urlLocation += window.location.search;
      }
      history.pushState( null, null, urlLocation );
    }

    // Remove ESC event listener
    document.removeEventListener( 'keyup', this.escListener );

    this._closeOverlay();
    // Reset body scroll
    // Modal
    this._afterTransitionModalListener = this._afterTransitionModal.bind( this );
    this.lastModal.addEventListener( 'transitionend', this._afterTransitionModalListener );
    this.lastModal.classList.remove( 'js-is--animated' );
  }

  /**
   * Close the first modal
   * @method _fetchContent
   */
  _fetchContent( url ) {
    fetch( url )
      .then(( resp ) => resp.text())
      .then(( text ) => {
        let html = document.createElement( 'html' );

        // SVG are not loaded if you are using DOMParser with text/html
        html.innerHTML = text;
        // check if a body exit then remove it
        if ( html.querySelector( 'body' )) {
          this.lastModal.innerHTML = html.querySelector( 'body' ).innerHTML;
        } else {
          this.lastModal.innerHTML = html.innerHTML;
        }

        // WE NEED TO THINK ABOUT SOMETHING TO SET THIS ATTRIBUTE, AS THE FETCHED CONTENT WON'T ALWAYS HAVE A .modal__title
        // this.lastModal.setAttribute('aria-describedby',this.lastModal.querySelector('.modal__title').id);
        // eslint-disable-next-line one-var
        let event = new CustomEvent( 'modalfetched', {
          detail: {
            fetchedURL: url
          }
        });
        this.lastModal.dispatchEvent( event );

        // REWORK THE CLOSING -- Needs a bit more of attention. This searches for dataCloses
        // eslint-disable-next-line one-var
        let dataClose = Array.from( document.querySelectorAll( '[data-dismiss="fetched-modal"]' ));
        if ( dataClose ) {
          dataClose.forEach( e => {
            e.addEventListener( 'click', this.close.bind( this ));
          });
        }
      }
      )
      .catch(( error ) => {
        // eslint-disable-next-line no-console
        console.error( 'Error with' + url + ': \n', error );
      });
  }


  /**
   * Set this.lastTrigger
   * @protected
   * @method _setCurrentTrigger
   */
  _setCurrentTrigger( trigger ) {
    this.lastTrigger = trigger;
  }

  /**
   * Load a modal by Hash/Id name
   * @protected
   * @method _openByHash
   */
  _openByHash() {
    const hash = window.location.hash.substring( 1 );
    let target;

    if ( window.location.hash.length > 1 ) {
      target = document.getElementById( hash );
    }
    // Get id in url and remove #
    // Search the ID

    // Check if the node exist
    if ( target ) {
      // Check ff this node is a modal
      if ( target.classList.contains( 'modal' )) {
        //Assign current trigger
        this._setCurrentTrigger( document.querySelector( '[data-target="' + target.id + '"]' ));
        //open a new modal
        this.open( target );
      }
    }
  }

  /**
   * Set correct size to the ajax modal
   * @protected
   * @method _setFetchModalSize
   */
  _setFetchModalSize() {
    if ( this.lastTrigger.hasAttribute( 'data-modal-size' )) {
      this.lastModal.classList.add( 'modal--' + this.lastTrigger.getAttribute( 'data-modal-size' ));
    }
  }

  /**
   * Get scrollbar width
   * @protected
   * @method _getScrollBarWidth
   * @return {number} scrollbar width
   */
  _getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /**
   * Build a new overlay
   * @protected
   * @method _buildOverlay
   * @return {Object} overlay
   */

  _buildOverlay() {
    const checkOverlay = document.querySelector( '.modal__overlay' );
    // Avoid double overlay
    if ( !checkOverlay ) {
      const overlay = document.createElement( 'div' );
      overlay.classList.add( 'modal__overlay' );
      // overlay.addEventListener('click', this.close.bind(this));

      document.body.style.paddingRight = this._getScrollbarWidth() + 'px';
      return document.body.appendChild( overlay );
    }
  }

  /**
   * Close the modal if the holder is clicked
   * @protected
   * @method _clickToClose
   * @param event
   */
  _clickToClose( event ) {
    if ( event.target.closest( '.modal' )) {
      return;
    }
    this.close( event );
  }

  /**
   * Close the modal if ESC key is pressed
   * @protected
   * @method _escToClose
   * @param event
   */
  _escToClose( event ) {
    if ( event.key === 'Escape' ) {
      this.close( event );
    }
  }


  /**
   * Show overlay
   * @protected
   * @method _openOverlay
   * @param {node} overlay
   */

  _openOverlay( overlay ) {
    overlay.classList.add( 'js-is--animated' );
  }


  /**
   * Close overlay
   * @protected
   * @method _closeOverlay
   */

  _closeOverlay() {
    const overlay = document.querySelector( '.modal__overlay' );
    // On transitionend remove the overlay
    if ( overlay ) {
      overlay.addEventListener( 'transitionend', () => {
        overlay.classList.remove( 'modal__overlay' );
        overlay.parentNode.removeChild( overlay );
      });
      overlay.classList.remove( 'js-is--animated' );
    }
  }


  /**
   * Launch after the modal transition
   * @protected
   * @method _afterTransitionModal
   */

  _afterTransitionModal() {
    if ( this.lastTrigger ) {

      if ( this.lastTrigger.hasAttribute( 'data-fetch-url' )) {
        this.lastModal.removeChild( this.lastModal.lastChild );
      }

      if ( this.lastTrigger.hasAttribute( 'data-fetch-url' )) {
        this.lastModal.classList.remove( 'modal--sm', 'modal--md', 'modal--lg' );
      }
      //reset lastTrigger
      this.lastTrigger = null;
    }

    // this.lastModal.classList.remove('js-is--open');
    this.lastModal.parentElement.parentElement.classList.remove( 'js-is--open' );
    this.lastModal.removeEventListener( 'transitionend', this._afterTransitionModalListener );
    this.lastModal.removeAttribute( 'aria-describedby' );



    this.lastModal = null;
    document.body.style.paddingRight = null;
    document.body.classList.remove( 'js-is--modal-open' );
  }
}
